import React, { useState } from "react";
import {
  Alert,
  Button,
  Container,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../../services/firebase";
import { useRole } from "../../contexts/RoleContext";
import { useChildContext } from "../../contexts/ChildContext";
import useMyStaffNotes from "./useMyStaffNotes";
import { listProfessionalDocumentation } from "./staffNotesService";
import {
  dateKeyInZone,
  formatStaffTime,
  summarizeStaffNotes,
} from "./staffNotePresentation";
import StaffOriginalSection from "./StaffOriginalSection";

export default function ProfessionalDocumentationPage() {
  const [user] = useAuthState(auth);
  const { childrenWithAccess = [], getUserRoleForChild } = useRole();
  const { currentChildId, setCurrentChildId } = useChildContext();
  const role = getUserRoleForChild?.(currentChildId);
  const enabled = Boolean(
    user && ["care_owner", "care_partner"].includes(role),
  );
  const data = useMyStaffNotes(
    currentChildId,
    enabled,
    user?.uid,
    listProfessionalDocumentation,
  );
  const [caregiver, setCaregiver] = useState(""),
    [day, setDay] = useState(""),
    [status, setStatus] = useState("all");
  const [opened, setOpened] = useState(null);
  const notes = data.notes.filter(
    (note) =>
      (!caregiver || note.authorId === caregiver) &&
      (!day ||
        (note.version &&
          dateKeyInZone(note.version.documentedAt, data.context.timeZone) ===
            day)) &&
      (status === "all" ||
        (status === "signed"
          ? Boolean(note.signature)
          : !note.signature && !note.withdrawn)),
  );
  const counts = summarizeStaffNotes(notes);
  const caregivers = [
    ...new Map(
      data.notes.map((note) => [
        note.authorId,
        note.authorName || note.authorId,
      ]),
    ).entries(),
  ];
  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Stack spacing={2}>
        <Button component={Link} to="/home" sx={{ alignSelf: "flex-start" }}>
          Back to Home
        </Button>
        <Typography variant="h4" component="h1">
          Professional Care Documentation
        </Typography>
        <TextField
          select
          label="Care profile"
          value={currentChildId || ""}
          onChange={(event) => {
            setCurrentChildId(event.target.value);
            setOpened(null);
            setCaregiver("");
          }}
        >
          {childrenWithAccess.map((child) => (
            <MenuItem key={child.id} value={child.id}>
              {child.name || child.firstName || "Care profile"}
            </MenuItem>
          ))}
        </TextField>
        {!enabled ? (
          <Alert severity="info">
            Shared documentation review is available to authorized Care Owners
            and Care Partners.
          </Alert>
        ) : (
          <>
            <Typography>
              Shared-profile documentation only. Facility-private documentation
              requires separate authorization.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField
                select
                label="Caregiver"
                value={caregiver}
                onChange={(event) => setCaregiver(event.target.value)}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="">All caregivers</MenuItem>
                {caregivers.map(([id, name]) => (
                  <MenuItem key={id} value={id}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Documentation date"
                type="date"
                value={day}
                InputLabelProps={{ shrink: true }}
                onChange={(event) => setDay(event.target.value)}
              />
              <TextField
                select
                label="Signature status"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                <MenuItem value="all">All records</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="signed">Signed</MenuItem>
              </TextField>
            </Stack>
            <Button onClick={data.refresh} disabled={data.loading}>
              Refresh documentation
            </Button>
            {data.error ? <Alert severity="error">{data.error}</Alert> : null}
            <Typography>
              {notes.length} records · {counts.signed} signed · {counts.pending}{" "}
              pending
            </Typography>
            {data.loading ? (
              <Typography>Loading documentation…</Typography>
            ) : null}
            {!data.loading && !notes.length ? (
              <Typography>No documentation in this view.</Typography>
            ) : null}
            {notes.map((note) => (
              <Paper key={note.captureId} variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1}>
                  <Typography fontWeight={700}>
                    {note.authorName || note.authorId}
                  </Typography>
                  <Typography>
                    {note.withdrawn
                      ? "Withdrawn"
                      : note.signature
                        ? `Signed · ${note.signature.signerName} · ${note.signature.providerTitle} · ${formatStaffTime(note.signature.signedAt, data.context.timeZone)}`
                        : "Pending signature"}
                  </Typography>
                  {note.parentCaptureId ? (
                    <Typography>
                      Dated correction to a signed original
                    </Typography>
                  ) : null}
                  {note.loadError ? (
                    <Alert severity="warning">{note.loadError}</Alert>
                  ) : null}
                  <Button
                    onClick={() =>
                      setOpened(
                        note.captureId === opened ? null : note.captureId,
                      )
                    }
                  >
                    Open original and audit history
                  </Button>
                  <Button component={Link} to={`/sources/${note.captureId}`}>
                    Open source
                  </Button>
                  {opened === note.captureId ? (
                    <StaffOriginalSection
                      key={`${currentChildId}-${user?.uid}-${opened}`}
                      source={{ id: note.captureId }}
                    />
                  ) : null}
                </Stack>
              </Paper>
            ))}
          </>
        )}
      </Stack>
    </Container>
  );
}
